import _ from 'lodash';

import { isEntityDetailsTable } from './buildEntityInformationTableDefinition';
import { isGroupVsRemainderLine } from './buildGroupVsRemainderDefinition';
import {
  followsReportBaseline,
  getLegacyMatrixComparison,
  isLegacyMatrixTable,
} from './buildLegacyMatrixDefinition';
import { isMetricListTable } from './buildMetricListDefinition';
import { isRemindersTable } from './buildRemindersDefinition';
import {
  getTableComparison,
  isRecordsTable,
  isTableFollowingReport,
} from './buildTableDefinition';
import { DEFAULT_VIS_TYPE } from './visTypes';

type ComparisonContent = Pick<
  Studio.ChartContent,
  'visType' | 'metricIds' | 'dimensions' | 'table' | 'comparison' | 'line'
>;

// A Line with a dimension and a single metric draws a line per term (see
// buildChartDefinition)
const isSplitLine = (content: ComparisonContent): boolean =>
  (content.dimensions ?? []).length > 0 &&
  (content.metricIds ?? []).length === 1;

// Whether the block's own comparison (Card.comparison) shows: on a Number,
// and on a Line not split by a dimension, as the smart card and time series
// draw them, and on a metric list, as the list's change column. Other Tables
// have their own (TableOptions.comparison). A Group vs remainder Line
// compares the selection with the rest instead. A Text block's placeholders
// show changes from it ({key.delta}).
export const canCompare = (content: ComparisonContent): boolean => {
  const visType = content.visType ?? DEFAULT_VIS_TYPE;
  return (
    visType === DEFAULT_VIS_TYPE ||
    visType === 'text' ||
    isMetricListTable(content) ||
    (visType === 'line' &&
      !isSplitLine(content) &&
      !isGroupVsRemainderLine(content))
  );
};

// A block comparison as a gadget stores it. Following the report stores
// none.
export const toPersistedComparison = (
  comparison: Studio.BlockComparison,
): PersistedComparisonType | undefined => {
  if (comparison.compareType === 'report') {
    return undefined;
  }
  return comparison.compareType === 'previous' && comparison.interval
    ? { compareType: 'previous', compareByInterval: comparison.interval }
    : { compareType: comparison.compareType };
};

// The comparison a block saves with, if it shows one of its own
export const getBlockComparison = (
  content: ComparisonContent,
): PersistedComparisonType | undefined => {
  // A metric list's change column compares with its own
  if (isMetricListTable(content)) {
    return content.comparison
      ? toPersistedComparison(content.comparison)
      : undefined;
  }
  if (content.visType === 'table') {
    // Records, reminders and entity details aren't compared. Date
    // comparison and pace tables compare with their baseline window.
    if (
      isRecordsTable(content) ||
      isRemindersTable(content) ||
      isEntityDetailsTable(content)
    ) {
      return undefined;
    }
    return isLegacyMatrixTable(content)
      ? getLegacyMatrixComparison(content.table)
      : getTableComparison(content);
  }
  return content.comparison && canCompare(content)
    ? toPersistedComparison(content.comparison)
    : undefined;
};

// Whether the block follows the report's comparison rather than its own
export const followsReportComparison = (content: ComparisonContent): boolean =>
  content.visType === 'table'
    ? followsReportBaseline(content) || isTableFollowingReport(content)
    : !!content.comparison &&
      content.comparison.compareType === 'report' &&
      canCompare(content);

// Only a report has a comparison to follow: elsewhere, following it is no
// comparison. A block's or element's own, and its Table's.
export const withoutReportComparison = <
  T extends {
    comparison?: { compareType: string };
    table?: Studio.TableOptions;
  },
>(
  content: T,
): T => {
  const { comparison, table } = content;
  const isComparingReport = comparison?.compareType === 'report';
  const isTableComparingReport = table?.comparison?.compareType === 'report';
  if (!isComparingReport && !isTableComparingReport) {
    return content;
  }
  const next = { ...content };
  if (isComparingReport) {
    delete next.comparison;
  }
  if (table && isTableComparingReport) {
    const rest = _.omit(table, 'comparison');
    if (_.isEmpty(rest)) {
      delete next.table;
    } else {
      next.table = rest;
    }
  }
  return next;
};

// Whether a date comparison or pace table compares with the report's
// comparison (elsewhere it compares with its default window)
export const followsReportBaselines = (
  table: Studio.TableOptions | undefined,
): boolean =>
  !!table &&
  (table.dateComparison?.baseline === 'report' ||
    table.pace?.baseline === 'report');

// A table's date comparison and pace options without following the report
export const withoutReportBaselines = (
  table: Studio.TableOptions | undefined,
): Studio.TableOptions | undefined => {
  if (!table || !followsReportBaselines(table)) {
    return table;
  }
  const without = <T extends { baseline?: Studio.TableBaseline }>(
    options: T | undefined,
  ): T | undefined => {
    if (!options || options.baseline !== 'report') {
      return options;
    }
    const rest = _.omit(options, 'baseline') as T;
    return _.isEmpty(rest) ? undefined : rest;
  };
  return _.omitBy(
    {
      ...table,
      dateComparison: without(table.dateComparison),
      pace: without(table.pace),
    },
    _.isUndefined,
  ) as Studio.TableOptions;
};

// Whether a block or element follows the report's comparison, its Table's
// included
export const isComparingReport = (content: {
  comparison?: { compareType: string };
  table?: Studio.TableOptions;
}): boolean => withoutReportComparison(content) !== content;

// A new Table follows the report's comparison, in its table options.
// Records, date comparison, pace, reminders and entity details tables have
// none.
const withTableDefault = <
  T extends Pick<Studio.ChartContent, 'visType' | 'table'>,
>(
  content: T,
): T =>
  content.visType === 'table' &&
  !content.table?.comparison &&
  !isRecordsTable(content) &&
  !isLegacyMatrixTable(content) &&
  !isRemindersTable(content) &&
  !isEntityDetailsTable(content)
    ? {
        ...content,
        table: { ...content.table, comparison: { compareType: 'report' } },
      }
    : content;

// A new Number or Line element follows the report's comparison, as a new
// Table element does
const withElementDefault = (
  element: Studio.CompositeElement,
): Studio.CompositeElement => {
  const visType = element.visType ?? DEFAULT_VIS_TYPE;
  return (visType === DEFAULT_VIS_TYPE || visType === 'line') &&
    !element.comparison
    ? { ...element, comparison: { compareType: 'report' } }
    : withTableDefault(element);
};

// A new block on a report follows the report's comparison, as a report
// gadget saved without one of its own does: a Number or Line, a Table (in
// its table options) and a Composite's Number, Line and Table elements
export const withDefaultComparison = <
  T extends ComparisonContent & { rows?: Studio.CompositeRow[] },
>({
  card,
  contentType,
}: {
  card: T;
  contentType: Studio.ContentType | undefined;
}): T => {
  if (contentType !== 'report') {
    return card;
  }
  const visType = card.visType ?? DEFAULT_VIS_TYPE;
  if (visType === 'composite') {
    return card.rows
      ? {
          ...card,
          rows: card.rows.map((row) => ({
            ...row,
            elements: row.elements.map(withElementDefault),
          })),
        }
      : card;
  }
  if (visType === 'table') {
    return withTableDefault(card);
  }
  return !card.comparison &&
    (visType === DEFAULT_VIS_TYPE || visType === 'line')
    ? { ...card, comparison: { compareType: 'report' } }
    : card;
};

// A block after a change, with a new block's comparison for what the change
// made new: a Composite's new elements, and a block or element that became
// a Table. What was already there keeps its comparison, none included.
export const withNewContentDefaults = ({
  previous,
  card,
  contentType,
}: {
  previous: Studio.Card | undefined;
  card: Studio.Card;
  contentType: Studio.ContentType | undefined;
}): Studio.Card => {
  if (contentType !== 'report' || previous === card) {
    return card;
  }
  if (!previous) {
    return withDefaultComparison({ card, contentType });
  }
  if (card.visType === 'table') {
    return previous.visType === 'table' ? card : withTableDefault(card);
  }
  if (card.visType !== 'composite' || !card.rows) {
    return card;
  }
  const before = new Map(
    (previous.visType === 'composite' ? previous.rows ?? [] : []).flatMap(
      (row) => row.elements.map((e) => [e.id, e] as const),
    ),
  );
  return {
    ...card,
    rows: card.rows.map((row) => ({
      ...row,
      elements: row.elements.map((element) => {
        const was = before.get(element.id);
        if (!was) {
          return withElementDefault(element);
        }
        return element.visType === 'table' && was.visType !== 'table'
          ? withTableDefault(element)
          : element;
      }),
    })),
  };
};
